import React from 'react'
import { useAtomValue } from 'jotai'
import dt from 'dayjs'
import Decimal from 'decimal.js'
import * as state from '../../states'
import {
	type TOfferSpendByOffer,
	type TOfferSpendByOfferAggregated,
	EOfferSpendByOfferView,
	TOfferSpendByOfferDay,
	TOfferSpendByOfferAggOnly,
	ERoleAccess
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	headCellFormatterSortable,
	ESortOrder,
	UiMoreTextInfo,
	UiTotalLabel
} from '../ui'
import { OFFER_SPEND_BY_OFFER_PAGE_SORT_FIELDS, IOfferSpendByOfferSortField } from '../../hooks'
import { Access } from '../../lib'
import { Day, Days, renderTotal, renderBy } from './OfferSpendByAccountListTable'
import * as helpers from '../../helpers'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IOfferSpendByOfferListTableProps = {
	viewBy: EOfferSpendByOfferView
	range: Array<dt.Dayjs>
	items: TOfferSpendByOffer[];
	skip: number;
	sortBy?: IOfferSpendByOfferSortField;
	setSortBy?: (sortBy: IOfferSpendByOfferSortField) => void;
	order?: ESortOrder;
	setOrder?: (order: ESortOrder) => void;
	onViewOfferSpendDetails?: (item: TOfferSpendByOfferDay | TOfferSpendByOfferAggOnly, e: React.MouseEvent) => void;
}

export const OfferSpendByOfferListTable = (props: IOfferSpendByOfferListTableProps) => {
	const {
		viewBy,
		range,
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onViewOfferSpendDetails
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)

	const tableConfig: IUiTableConfig<TOfferSpendByOffer | null>[] = [
		{
			key: 'offerId',
			title: t(ALL_LANG_KEY.offer_spend_by_offer_list_table.column_offer_id),
			rowCellFormatter({ rowData }) {
				if (!rowData) {
					return ''
				}
				return rowData.offerId
			}
		},
		{
			key: 'offer.name',
			title: t(ALL_LANG_KEY.offer_spend_by_offer_list_table.column_offer),
			rowCellFormatter: ({ rowData }) => {
				if (!rowData) {
					return ''
				}
				const offer = rowData?.offer ?? null
				const name = offer?.name ?? null

				return <>{name ?? '?'}</>
			}
		},
		{
			key: 'country',
			title: t(ALL_LANG_KEY.offer_spend_by_offer_list_table.column_country),
			classesToAdd: {
				th: 'border-end',
				td: 'border-end'
			},
			rowCellFormatter: ({ value, rowData }) => {
				if (!rowData) {
					return <UiTotalLabel/>
				}

				const val = rowData?.offer?.country
				const valueFormat: Array<string> = val ?? []

				return <UiMoreTextInfo value={valueFormat}/>
			}
		},
		{
			key: 'amount',
			title: t(ALL_LANG_KEY.offer_spend_by_offer_list_table.column_amount),
			classesToAdd: {
				th: 'text-center'
			},
			rowCellFormatter: ({ rowData, rowsData, rowIndex, cellIndex }) => {
				if (!rowData) {
					const total = rowsData
						.map(_ => _?.amount ?? 0)
						.reduce((sum, amount) => sum.add(amount), Decimal(0))
					return renderTotal(total, rowIndex, cellIndex)
				}
				const total = Decimal(rowData.amount)
				return renderTotal(total, rowIndex, cellIndex)
			}
		},
		...range.map((date: dt.Dayjs, index: number): IUiTableConfig<TOfferSpendByOffer | null> => {
			const day = viewBy === EOfferSpendByOfferView.Month ? date.format('MM.YYYY') : date.format('DD.MM.YYYY')

			return {
				key: `agg-${day}-${index}`,
				title: `${day}`,
				headCellFormatter() {
					return <Days><Day>{day}</Day></Days>
				},
				rowCellFormatter: ({ rowData, rowsData, rowIndex, cellIndex }) => {
					const unit = viewBy
					const filterFunc = (item: TOfferSpendByOfferAggregated) => {
						return dt.utc(item.at).startOf(unit).diff(date, unit) === 0
					}

					if (!rowData) {
						const agg = rowsData.map((rowData): TOfferSpendByOfferAggregated[] => {
							const allAgg = rowData?.agg ?? []
							const agg = allAgg.filter(filterFunc)
							return agg
						})

						const aggFlat = agg.flat()
						const total = aggFlat
							.map(_ => _?.amount ?? 0)
							.reduce((sum, amount) => sum.add(amount), Decimal(0))
						return renderTotal(total, rowIndex, cellIndex)
					}
					const allAgg = rowData.agg
					const agg = allAgg.filter(filterFunc)
					return renderBy($access.$(ERoleAccess.OfferSpendByOfferList), onViewOfferSpendDetails)(agg, rowData, rowIndex, cellIndex, index, viewBy, day)
				}
			}
		})
	]

	const tableConfigSortable: IUiTableConfig<TOfferSpendByOffer>[] = tableConfig.map(
		(configItem) => {
			const configItemIsSortable = OFFER_SPEND_BY_OFFER_PAGE_SORT_FIELDS.includes(configItem.key as IOfferSpendByOfferSortField)

			if (!configItemIsSortable || !sortBy || !setSortBy || !order || !setOrder) { // ?!?!
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('SpendListTable:: ', { pendListTableConfig, roleListTableConfigSortable })
	const { dataItems, tableClassName } = helpers.getTableItemsWithTotalRows(items)

	return <>
		<UiTable
			data={dataItems}
			config={tableConfigSortable}
			// TODO: uncomment if necessary
			classesToAdd={{
				//wrapper: 'table-sticky-wrapper',
				table: tableClassName
			}}
		/>
	</>
}